/* ══════════════════════════════════════════════════════════
 *  usePermissions — Role-based access control hook
 *  Mirrors FE/Admin/permission-check.js for the React app.
 *
 *  Fetches GET /api/users/permissions and exposes:
 *    • role, features, allowedCaseTypes
 *    • hasPermission(name)  / hasAnyPermission(names)
 *    • canAccessAdmin       — quick boolean for route guards
 *    • visibleAdminSections — filtered section list
 * ══════════════════════════════════════════════════════════ */

import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { isSuperadminOrAbove } from '../utils/roleHierarchy';
import type { AdminSection } from '../types/admin';

/* ── Types ── */

export interface PermissionFeature {
  feature_name: string;
  category?: string;
  description?: string;
}

interface PermissionsResponse {
  success: boolean;
  role: string;
  username: string;
  permissions: PermissionFeature[];
  allowed_case_types: string[] | null;
}

export interface PermissionsData {
  role: string;
  features: PermissionFeature[];
  allowedCaseTypes: string[] | null;
  loaded: boolean;
}

/* ── Section → required features map ── */

export const SECTION_PERMISSIONS: Record<string, string[]> = {
  // Admin section id  →  required feature_name(s)  (user needs ANY one)
  overview: ['admin_page_access'],
  users: ['manage_users'],
  pipelines: ['manage_pipelines'],
  fields: ['configure_fields'],
  defendants: ['edit_defendant'],
  tickets: ['view_issues'],
  'data-import': ['data_import'],
  'document-upload': ['document_upload'],
  ledger: ['document_print_ledger'],
  reports: ['view_reports'],
  'recycle-bin': ['manage_recycle_bin'],
  'draft-templates': ['manage_draft_templates'],
  'email-templates': ['manage_email_templates'],
  blp: ['manage_blp'],
  automations: ['manage_automations'],
  'data-quality': ['manage_data_quality'],
  'client-portal': ['manage_client_portal'],
  'firm-branding': ['manage_firm_branding'],
};

/** Sections only a superadmin may access — no feature grant can unlock these. */
const SUPERADMIN_ONLY_SECTIONS = new Set(['bulk-reassign', 'system-config']);

/* ── Query function ── */

async function fetchPermissions(): Promise<PermissionsData> {
  try {
    const { data } = await api.get<PermissionsResponse>('/api/users/permissions');
    return {
      role: data.role || 'user',
      features: data.permissions || [],
      allowedCaseTypes: data.allowed_case_types ?? null,
      loaded: true,
    };
  } catch (err) {
    console.error('[usePermissions] Failed to fetch permissions:', err);
    // Fallback: assume admin so we don't lock users out (same as old FE)
    return { role: 'admin', features: [], allowedCaseTypes: null, loaded: true };
  }
}

/* ── Hook ── */

export function usePermissions() {
  const { data, isLoading } = useQuery<PermissionsData>({
    queryKey: ['user-permissions'],
    queryFn: fetchPermissions,
    staleTime: 5 * 60 * 1000, // 5 min, then soft-refetch
    gcTime: 10 * 60 * 1000, // keep in cache 10 min
    retry: 1,
  });

  const perms: PermissionsData = data ?? {
    role: 'user',
    features: [],
    allowedCaseTypes: null,
    loaded: false,
  };

  /* ── helpers ── */

  // Superadmins and System Owners pass every feature check (same rule as the server)
  const isSuperAdmin = isSuperadminOrAbove(perms.role);

  /** Does the user have a specific feature permission? */
  function hasPermission(featureName: string): boolean {
    if (isSuperAdmin) return true;
    return perms.features.some((f) => f.feature_name === featureName);
  }

  /** Does the user have ANY of the listed features? */
  function hasAnyPermission(featureNames: string[]): boolean {
    if (isSuperAdmin) return true;
    return featureNames.some((name) => hasPermission(name));
  }

  /** Can this user see the admin area at all? */
  const canAccessAdmin =
    isSuperAdmin || perms.role === 'admin' || hasPermission('admin_page_access');

  /** Filter a list of AdminSections to only those the user may access. */
  function filterSections(sections: AdminSection[]): AdminSection[] {
    if (isSuperAdmin) return sections;
    // Admin role sees all sections except superadmin-only ones — same access
    // ceiling as before Phase 1 (the BE enforces feature-level auth independently).
    if (perms.role === 'admin') {
      return sections.filter((s) => !SUPERADMIN_ONLY_SECTIONS.has(s.id));
    }
    return sections.filter((s) => {
      // Superadmin-only sections are never shown to non-superadmins
      if (SUPERADMIN_ONLY_SECTIONS.has(s.id)) return false;
      const required = SECTION_PERMISSIONS[s.id];
      // If no mapping exists, default to requiring admin_page_access
      return required ? hasAnyPermission(required) : hasPermission('admin_page_access');
    });
  }

  return {
    ...perms,
    isLoading,
    isSuperAdmin,
    isAdmin: perms.role === 'admin' || isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    canAccessAdmin,
    filterSections,
  };
}
